import type { ContainerLevelHistory, Dispatch, TransportAssoc } from "@ots-frontend/ots-data";
import type { AuditObjectAuditSummary } from "@ots-frontend/ots-data";
import { activityIdText } from "../driver-activity/model/activity-types";
import { buildDriverActivity } from "../driver-activity/model/activity-build";
import { flattenEntries, isActivityTicket } from "../driver-activity/model/activity-entries";
import type { ActivityEntry } from "../driver-activity/model/activity-types";

const HOUR = 3_600_000;

/** How far back the panel looks. */
export type ContainerDetailsRange = "24h" | "3d" | "7d";

export const containerDetailsRangeMillis: Record<ContainerDetailsRange, number> = {
  "24h": 24 * HOUR,
  "3d": 72 * HOUR,
  "7d": 168 * HOUR,
};

/** Oil or water: what a reading's cargo code means for its color and its own drops. Codes vary ("Oil", "PRODUCED_WATER"). */
export type ContainerCargo = "oil" | "water";

export const containerCargoOf = (code: string | undefined): ContainerCargo => /oil/i.test(code ?? "") ? "oil" : "water";

/** A fall in a tank's SCADA volume big enough to be a haul. */
export type ContainerHaulDrop = {
  cargo: ContainerCargo;
  start: number;
  end: number;
  fromVolume: number;
  toVolume: number;
  /** How much the volume fell. */
  volume: number;
  uom?: string;
  /** Pickup tickets written from this tank around the drop; none means the haul has no ticket. */
  tickets: ActivityEntry[];
};

export type HaulDropOptions = {
  /** The smallest fall counted as a haul. */
  minDrop?: number;
  /** Wobble in the readings ignored while the volume falls. */
  noise?: number;
};

/**
 * Falls in the SCADA volume, oldest first, each cargo on its own so oil and water readings never read as a fall. A
 * fall runs while each reading stays within `noise` of the lowest so far, so meter jitter neither ends a haul early
 * nor counts as one, and a fall that springs back by more than half is a glitch, not a haul.
 */
export const detectHaulDrops = (levels: readonly ContainerLevelHistory[], options: HaulDropOptions = {}): Omit<ContainerHaulDrop, "tickets">[] => {
  const byCargo = new Map<ContainerCargo, ContainerLevelHistory[]>();
  for (const reading of levels) {
    if (reading.type !== "Meter" || reading.volume == null) continue;
    const cargo = containerCargoOf(reading.cargoSubtype);
    byCargo.set(cargo, [...(byCargo.get(cargo) ?? []), reading]);
  }
  return [...byCargo].flatMap(([cargo, readings]) => cargoDrops(cargo, readings, options)).sort((left, right) => left.start - right.start);
};

const cargoDrops = (cargo: ContainerCargo, levels: ContainerLevelHistory[], { minDrop = 20, noise = 2 }: HaulDropOptions): Omit<ContainerHaulDrop, "tickets">[] => {
  const readings = levels
    .map((reading) => ({ time: reading.createDttm, volume: reading.volume ?? 0, uom: reading.volumeUom }))
    .sort((left, right) => left.time - right.time);

  const drops: Omit<ContainerHaulDrop, "tickets">[] = [];
  let index = 0;
  while (index < readings.length - 1) {
    if (readings[index + 1].volume >= readings[index].volume - noise) {
      index++;
      continue;
    }
    const start = readings[index];
    let low = readings[index + 1];
    let next = index + 2;
    while (next < readings.length && readings[next].volume <= low.volume + noise) {
      if (readings[next].volume < low.volume) low = readings[next];
      next++;
    }
    const volume = start.volume - low.volume;
    // A tank can't refill half a haul in one reading, so a fall that springs straight back is a meter glitch.
    const after = readings[next];
    const glitch = after != null && after.volume - low.volume > volume / 2;
    if (volume >= minDrop && !glitch) {
      drops.push({ cargo, start: start.time, end: low.time, fromVolume: start.volume, toVolume: low.volume, volume, uom: start.uom ?? low.uom });
    }
    index = Math.max(next - 1, index + 1);
  }
  return drops;
};

/** Tickets picked up from this tank, newest first. */
export const pickupsFrom = (entries: readonly ActivityEntry[], containerId: string) =>
  entries.filter((entry) => entry.pickup?.containerId === containerId);

/** Each drop with the pickups written within `window` of it; a ticket counts towards one drop only. */
export const matchHaulDrops = (
  drops: readonly Omit<ContainerHaulDrop, "tickets">[],
  pickups: readonly ActivityEntry[],
  window = 2 * HOUR,
): ContainerHaulDrop[] => {
  const used = new Set<string>();
  return drops.map((drop) => {
    const tickets = pickups.filter((entry) => !used.has(entry.key) && entry.time != null
      && entry.time >= drop.start - window && entry.time <= drop.end + window);
    tickets.forEach((entry) => used.add(entry.key));
    return { ...drop, tickets };
  });
};

/** The ticket's resource ID, or its paper ticket number when it has none. */
export const containerTicketId = (entry: ActivityEntry) => activityIdText(entry) ?? activityIdText(entry, "ticketNumber");

/** Who a ticket row names: HubGo cares which hauler ran the load, DispatchGo which driver. */
export type ContainerTicketParty = "hauler" | "driver";

type HaulerNames = { parentCompanyName?: string; trailerParentCompanyName?: string; trailerCompanyName?: string };

/** The hauler on a stop, as the ticket grid's Truck Company column reads it (the raw and the entity field names). */
export const haulerNameOf = (stop: object | undefined) => {
  const names = stop as HaulerNames | undefined;
  return names?.trailerParentCompanyName ?? names?.parentCompanyName ?? names?.trailerCompanyName;
};

/** Search results as Driver Activity rows, newest first, with each ticket's audit when loaded. */
export const toContainerTicketEntries = (ttas: readonly TransportAssoc[] | undefined, audits?: readonly AuditObjectAuditSummary[]): ActivityEntry[] => {
  const auditsById = audits && new Map(audits.map((audit) => [audit.target.objectId, audit]));
  return flattenEntries(buildDriverActivity((ttas ?? []).map((tta) => ({ transportTransportAssoc: tta })), { audits: auditsById }))
    .filter(isActivityTicket)
    .sort((left, right) => (right.time ?? 0) - (left.time ?? 0));
};

export type ContainerDetailsSummary = {
  /** Pickups from this tank since the start of the day. */
  loadsToday: number;
  hauledToday: number;
  hauledUom?: string;
  lastHaul?: ActivityEntry;
  /** How much the tank has filled since its last haul ended, from SCADA. */
  fillSinceLastHaul?: number;
  fillUom?: string;
  /** A dispatch still waiting on or underway, when the latest one is. */
  openDispatch?: Dispatch;
};

const OPEN_DISPATCH = new Set(["Pending", "Started"]);

export const isDispatchOpen = (dispatch: Pick<Dispatch, "status"> | undefined) => !!dispatch && OPEN_DISPATCH.has(dispatch.status);

export const summarizeContainerDetails = ({
  pickups,
  drops,
  levels,
  dispatch,
  dayStart,
}: {
  pickups: readonly ActivityEntry[];
  drops: readonly ContainerHaulDrop[];
  levels: readonly ContainerLevelHistory[];
  dispatch?: Dispatch;
  dayStart: number;
}): ContainerDetailsSummary => {
  const today = pickups.filter((entry) => (entry.time ?? 0) >= dayStart);
  const lastDrop = drops[drops.length - 1];
  // The fill is the last-hauled cargo's: how far its own reading has come back since.
  const latest = levels
    .filter((reading) => reading.type === "Meter" && reading.volume != null && (!lastDrop || containerCargoOf(reading.cargoSubtype) === lastDrop.cargo))
    .reduce<ContainerLevelHistory | undefined>((newest, reading) => !newest || reading.createDttm > newest.createDttm ? reading : newest, undefined);
  return {
    loadsToday: today.length,
    hauledToday: today.reduce((sum, entry) => sum + (entry.pickup?.quantityDecimal ?? 0), 0),
    hauledUom: today.find((entry) => entry.pickup?.quantityUnitOfMeasure)?.pickup?.quantityUnitOfMeasure,
    lastHaul: pickups[0],
    fillSinceLastHaul: lastDrop && latest && latest.createDttm >= lastDrop.end ? Math.max(latest.volume! - lastDrop.toVolume, 0) : undefined,
    fillUom: latest?.volumeUom ?? lastDrop?.uom,
    openDispatch: isDispatchOpen(dispatch) ? dispatch : undefined,
  };
};
